import React, { useMemo, useState } from "react";
import { Checkbox, Table, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import { PihTableShell, renderPihColumnTitle, type SortOrder } from "../../common/pihStyleTable";
import "../../common/pih-style-table.css";
import ConfirmDialog from "../components/ConfirmDialog";
import CcButton from "../components/CcButton";
import {
  deltaCycleClass,
  formatConfigurationDate,
  type MeasurementRecord,
} from "../../../web-app";

interface MeasurementsListProps {
  records: MeasurementRecord[];
  onLogMeasurement: () => void;
  onEditMeasurement: (id: string) => void;
  onDeleteMeasurement: (id: string) => void;
  onToggleInFit: (record: MeasurementRecord) => void;
  onReviewCalibration: () => void;
  reviewCalibrationDisabled?: boolean;
}

function getCellSearchValue(record: MeasurementRecord, key: string): string {
  const predicted = record.predictedHeatingCycleTimeMinutes;
  const measured = record.measuredHeatingCycleTimeMinutes;
  const deltaPct =
    measured !== undefined && predicted !== null && predicted !== 0
      ? String(Math.round(((measured - predicted) / predicted) * 100))
      : "";

  switch (key) {
    case "loggedAt":
      return formatConfigurationDate(record.loggedAt);
    case "reference":
      return record.configurationReference;
    case "powerSource":
      return record.powerSourceLabel ?? "";
    case "predictedCycle":
      return predicted !== null ? predicted.toFixed(1) : "";
    case "measuredCycle":
      return measured !== undefined ? measured.toFixed(1) : "";
    case "deltaCycle":
      return deltaPct;
    case "temperature":
      return String(record.measuredTemperatureC);
    case "inFit":
      return record.inFit ? "yes" : "no";
    default:
      return "";
  }
}

function formatDeltaCycle(record: MeasurementRecord): React.ReactNode {
  const predicted = record.predictedHeatingCycleTimeMinutes;
  const measured = record.measuredHeatingCycleTimeMinutes;
  if (measured === undefined || predicted === null || predicted === 0) {
    return "—";
  }
  const deltaPct = ((measured - predicted) / predicted) * 100;
  const deltaClass = deltaCycleClass(predicted, measured);
  const sign = deltaPct > 0 ? "+" : "";
  return (
    <span className={["mono-value", deltaClass].filter(Boolean).join(" ")}>
      {sign}
      {Math.round(deltaPct)}%
    </span>
  );
}

const MeasurementsList = ({
  records,
  onLogMeasurement,
  onEditMeasurement,
  onDeleteMeasurement,
  onToggleInFit,
  onReviewCalibration,
  reviewCalibrationDisabled = false,
}: MeasurementsListProps) => {
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<string>("loggedAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("descend");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<MeasurementRecord | null>(null);

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleColumnSort = (key: string) => {
    if (sortField !== key) {
      setSortField(key);
      setSortOrder("ascend");
    } else if (sortOrder === "ascend") {
      setSortOrder("descend");
    } else if (sortOrder === "descend") {
      setSortField("loggedAt");
      setSortOrder("descend");
    } else {
      setSortOrder("ascend");
    }
    setPage(1);
  };

  const titleOptions = (dataIndex: string) => ({
    columnFilters,
    onColumnFilterChange: handleColumnFilterChange,
    sortField,
    sortOrder,
    onColumnSort: handleColumnSort,
  });

  const filteredRows = useMemo(() => {
    return records.filter((row) => {
      return Object.entries(columnFilters).every(([key, value]) => {
        const query = value.trim().toLowerCase();
        if (!query) return true;
        return getCellSearchValue(row, key).toLowerCase().includes(query);
      });
    });
  }, [records, columnFilters]);

  const sortedRows = useMemo(() => {
    if (!sortField || !sortOrder) return filteredRows;
    const isDesc = sortOrder === "descend";
    return [...filteredRows].sort((a, b) => {
      if (sortField === "loggedAt") {
        const aTime = a.loggedAt instanceof Date ? a.loggedAt.getTime() : new Date(a.loggedAt).getTime();
        const bTime = b.loggedAt instanceof Date ? b.loggedAt.getTime() : new Date(b.loggedAt).getTime();
        return isDesc ? bTime - aTime : aTime - bTime;
      }
      const aVal = getCellSearchValue(a, sortField);
      const bVal = getCellSearchValue(b, sortField);
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return isDesc ? -cmp : cmp;
    });
  }, [filteredRows, sortField, sortOrder]);

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize]);

  const handleDelete = (record: MeasurementRecord) => {
    setDeleteTarget(record);
  };

  const columns = [
    {
      title: renderPihColumnTitle("Date", "loggedAt", titleOptions("loggedAt")),
      dataIndex: "loggedAt",
      key: "loggedAt",
      width: 140,
      sorter: true,
      sortOrder: sortField === "loggedAt" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => formatConfigurationDate(record.loggedAt),
    },
    {
      title: renderPihColumnTitle("Reference", "reference", titleOptions("reference")),
      dataIndex: "reference",
      key: "reference",
      width: 200,
      sorter: true,
      sortOrder: sortField === "reference" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => record.configurationReference,
    },
    {
      title: renderPihColumnTitle("Power Source", "powerSource", titleOptions("powerSource")),
      dataIndex: "powerSource",
      key: "powerSource",
      width: 220,
      sorter: true,
      sortOrder: sortField === "powerSource" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => record.powerSourceLabel ?? "—",
    },
    {
      title: renderPihColumnTitle("Predicted Cycle", "predictedCycle", titleOptions("predictedCycle")),
      dataIndex: "predictedCycle",
      key: "predictedCycle",
      width: 150,
      sorter: true,
      sortOrder: sortField === "predictedCycle" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => {
        const predicted = record.predictedHeatingCycleTimeMinutes;
        return (
          <span className="mono-value">
            {predicted !== null ? `${predicted.toFixed(1)} min` : "—"}
          </span>
        );
      },
    },
    {
      title: renderPihColumnTitle("Measured Cycle", "measuredCycle", titleOptions("measuredCycle")),
      dataIndex: "measuredCycle",
      key: "measuredCycle",
      width: 150,
      sorter: true,
      sortOrder: sortField === "measuredCycle" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => {
        const measured = record.measuredHeatingCycleTimeMinutes;
        return (
          <span className="mono-value">
            {measured !== undefined ? `${measured.toFixed(1)} min` : "—"}
          </span>
        );
      },
    },
    {
      title: renderPihColumnTitle("Δ Cycle", "deltaCycle", titleOptions("deltaCycle")),
      dataIndex: "deltaCycle",
      key: "deltaCycle",
      width: 110,
      sorter: true,
      sortOrder: sortField === "deltaCycle" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => formatDeltaCycle(record),
    },
    {
      title: renderPihColumnTitle("Temp Reached", "temperature", titleOptions("temperature")),
      dataIndex: "temperature",
      key: "temperature",
      width: 130,
      sorter: true,
      sortOrder: sortField === "temperature" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => (
        <span className="mono-value">{record.measuredTemperatureC} °C</span>
      ),
    },
    {
      title: renderPihColumnTitle("In Fit", "inFit", titleOptions("inFit")),
      dataIndex: "inFit",
      key: "inFit",
      width: 90,
      align: "center" as const,
      sorter: true,
      sortOrder: sortField === "inFit" ? sortOrder ?? undefined : undefined,
      render: (_: unknown, record: MeasurementRecord) => (
        <Checkbox
          className="measurement-in-fit-checkbox"
          checked={record.inFit}
          aria-label="Include in calibration fit"
          onChange={() => onToggleInFit(record)}
        />
      ),
    },
    {
      title: (
        <div className="column-header-with-filter">
          <div className="column-header-text-wrapper">
            <span>Actions</span>
          </div>
        </div>
      ),
      key: "actions",
      fixed: "right" as const,
      width: 100,
      render: (_: unknown, record: MeasurementRecord) => (
        <div className="heat-profile-actions-cell">
          <Tooltip title="Edit">
            <button
              type="button"
              className="heat-profile-action-btn"
              onClick={() => onEditMeasurement(record.id)}
              aria-label={`Edit measurement for ${record.configurationReference}`}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              type="button"
              className="heat-profile-action-btn heat-profile-action-btn--danger"
              onClick={() => handleDelete(record)}
              aria-label={`Delete measurement for ${record.configurationReference}`}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<MeasurementRecord> | SorterResult<MeasurementRecord>[],
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = singleSorter?.field ?? singleSorter?.columnKey;
    if (field != null && singleSorter?.order) {
      setSortField(String(Array.isArray(field) ? field[0] : field));
      setSortOrder(singleSorter.order as SortOrder);
      setPage(1);
    }
    if (pagination.current) setPage(pagination.current);
    if (pagination.pageSize) setPageSize(pagination.pageSize);
  };

  return (
    <PihTableShell
      title="Measurement Records"
      description="Logged field measurements compared against predicted heating cycle and power."
      itemCount={sortedRows.length}
      actions={
        <>
          <CcButton
            variant="secondary"
            id="review-calibration-button"
            disabled={reviewCalibrationDisabled}
            onClick={onReviewCalibration}
          >
            Review suggested calibration
          </CcButton>
          <CcButton variant="primary" icon={<PlusOutlined />} onClick={onLogMeasurement}>
            Log measurement
          </CcButton>
        </>
      }
    >
      <Table<MeasurementRecord>
        className="pipeline-table heat-profile-table"
        columns={columns}
        dataSource={paginatedRows}
        rowKey="id"
        scroll={{ x: 1720 }}
        pagination={{
          current: page,
          pageSize,
          total: sortedRows.length,
          showSizeChanger: true,
          pageSizeOptions: ["6", "10", "20", "50"],
          showTotal: (total) => `${total} items`,
        }}
        onChange={handleTableChange}
        locale={{ emptyText: "No measurements logged yet." }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Remove Measurement"
        message={
          deleteTarget
            ? `Are you sure you want to remove the measurement for "${deleteTarget.configurationReference}"? This cannot be undone.`
            : ""
        }
        confirmLabel="Remove"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) onDeleteMeasurement(deleteTarget.id);
          setDeleteTarget(null);
        }}
      />
    </PihTableShell>
  );
};

export default MeasurementsList;
